import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { interviewApi } from '@/api';
import {
  Alert,
  Button,
  Checkbox,
  Icon,
  IconButton,
  Input,
  Modal,
  Textarea,
  useToast,
} from '@/components/ui';
import { interviewWhen } from '@/features/interviews/status';
import { errorMessage } from '@/hooks/useApiError';
import { cn } from '@/utils/cn';

import {
  MAX_SLOTS,
  NOTE_MAX,
  REASON_MAX,
  REASON_MIN,
  emptySlot,
  validateRescheduleRequest,
} from './reschedule';

/** "2026-10-16" for a date input, `days` from today. */
function dateInputValue(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

/**
 * The candidate asks the company for another time: a reason, up to three
 * alternatives (or "any time works"), and an optional note. Nothing about the
 * interview changes until the company answers.
 */
export function RescheduleRequestDialog({ interview, onClose }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [slots, setSlots] = useState([emptySlot()]);
  const [anyTime, setAnyTime] = useState(false);
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const check = validateRescheduleRequest({ reason, slots, anyTime, note });
  // Errors show once the student has tried to send, so an empty form does not
  // open covered in red.
  const errors = submitted ? check.errors : null;

  const send = useMutation({
    mutationFn: (payload) => interviewApi.requestReschedule(interview.id, payload),
    onSuccess: () => {
      toast.success(
        'Request sent. Your interview stays at its current time until the company answers.',
      );
      void queryClient.invalidateQueries({ queryKey: ['student-interviews'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not send your request')),
  });

  function updateSlot(index, field, value) {
    setSlots((current) =>
      current.map((slot, position) => (position === index ? { ...slot, [field]: value } : slot)),
    );
  }

  function submit(event) {
    event.preventDefault();
    setSubmitted(true);
    if (check.valid) send.mutate(check.payload);
  }

  const reasonLength = reason.trim().length;
  // Later today is fine: the only rule is that the time has not passed.
  const minDate = dateInputValue(0);

  return (
    <Modal
      open
      size="lg"
      title="Request another time"
      description={`${interview.title} · now ${interviewWhen(interview)}`}
      onClose={onClose}
    >
      <form className="space-y-5" onSubmit={submit} noValidate>
        <Alert tone="info">
          Your interview stays booked at its current time until{' '}
          {interview.company_name ?? 'the company'} answers. You can ask{' '}
          {interview.reschedule_requests_left === 1 ? 'once more' : 'twice'} for this interview.
        </Alert>

        <div>
          <Textarea
            label="Why do you need a different time?"
            required
            rows={3}
            maxLength={REASON_MAX}
            placeholder="I have a university exam that morning."
            value={reason}
            error={errors?.reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <p
            className={cn(
              'mt-1 text-right text-xs tabular-nums',
              reasonLength > 0 && reasonLength < REASON_MIN ? 'text-warning-700' : 'text-ink-muted',
            )}
            aria-live="polite"
          >
            {reasonLength}/{REASON_MAX}
            {reasonLength < REASON_MIN && ` · at least ${REASON_MIN}`}
          </p>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-ink text-[12px] font-medium">
            Times that work for you{' '}
            <span className="text-ink-muted font-normal">(up to {MAX_SLOTS})</span>
          </legend>
          {!anyTime && (
            <ol className="space-y-2">
              {slots.map((slot, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-ink-muted mt-2 w-14 shrink-0 text-xs font-medium">
                    Option {index + 1}
                  </span>
                  <Input
                    label={`Option ${index + 1} date`}
                    hideLabel
                    type="date"
                    min={minDate}
                    value={slot.date}
                    wrapperClassName="flex-1"
                    error={errors?.slots[index]}
                    onChange={(event) => updateSlot(index, 'date', event.target.value)}
                  />
                  <Input
                    label={`Option ${index + 1} start time`}
                    hideLabel
                    type="time"
                    step={900}
                    value={slot.time}
                    wrapperClassName="w-32"
                    onChange={(event) => updateSlot(index, 'time', event.target.value)}
                  />
                  {slots.length > 1 && (
                    <IconButton
                      icon="close"
                      variant="ghostDanger"
                      label={`Remove option ${index + 1}`}
                      onClick={() =>
                        setSlots((current) => current.filter((_, position) => position !== index))
                      }
                    />
                  )}
                </li>
              ))}
            </ol>
          )}
          {!anyTime && slots.length < MAX_SLOTS && (
            <Button
              type="button"
              variant="ghostBrand"
              size="sm"
              onClick={() => setSlots((current) => [...current, emptySlot()])}
            >
              <Icon name="plus" size={14} />
              Add another time
            </Button>
          )}
          <Checkbox
            label="Any time works, please suggest one"
            checked={anyTime}
            onChange={(event) => setAnyTime(event.target.checked)}
          />
          {errors?.slotsGeneral && (
            <p className="text-caption text-danger-600" role="alert">
              {errors.slotsGeneral}
            </p>
          )}
          <p className="text-ink-muted text-xs">
            In your own time zone. Later today works too, as long as the time has not passed.
          </p>
        </fieldset>

        <Textarea
          label="Anything else? (optional)"
          rows={2}
          maxLength={NOTE_MAX}
          placeholder="Any afternoon that week also works."
          value={note}
          error={errors?.note}
          onChange={(event) => setNote(event.target.value)}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button type="submit" isLoading={send.isPending}>
            <Icon name="send" size={14} />
            Send request
          </Button>
        </div>
      </form>
    </Modal>
  );
}
